import { useDebounce } from '@avanta/shared/hooks/useDebounce';
import { useServerPaginatedTable } from '@avanta/shared/hooks/useServerPaginatedTable';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useParams } from 'react-router';
import { QUERY_KEYS } from 'constants/common';
import { SurveyService } from 'services/surveys';
import { type PatientSurveyHistoryItem } from 'types/surveys';

const PAGE_SIZE = 10;

interface UseTestsPatientHistoryResult {
  table: ReturnType<typeof useServerPaginatedTable<PatientSurveyHistoryItem>>;
  tests: PatientSurveyHistoryItem[];
  totalCount: number;
  isLoading: boolean;
}

export const useTestsPatientHistory = (
  columns: ColumnDef<PatientSurveyHistoryItem>[]
): UseTestsPatientHistoryResult | null => {
  const params = useParams();
  const patientId = params?.id;
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [globalFilter, setGlobalFilter] = useState('');
  const debouncedFilter = useDebounce(globalFilter, 500);

  const testsQuery: UseQueryResult<PatientSurveyHistoryItem[], Error> = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_PATIENT_SURVEY_HISTORY,
      patientId,
      pagination.pageIndex,
      pagination.pageSize,
      debouncedFilter,
    ],
    queryFn: async () =>
      await SurveyService.getPatientSurveyHistory({
        userId: patientId ?? '',
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sort: 'completedAt,desc',
      }),
    select: response => response.data.filter(test => test.completed && test.completedAt),
    placeholderData: previousData => previousData,
    enabled: !!patientId,
  });

  const { data: totalCount = 0 } = useQuery({
    queryKey: [QUERY_KEYS.FETCH_PATIENT_SURVEY_HISTORY, patientId, 'count'],
    queryFn: async () =>
      await SurveyService.getPatientSurveyHistoryCount({
        userId: patientId ?? '',
      }),
    select: response => response.data.count,
    enabled: !!patientId,
  });

  const table = useServerPaginatedTable<PatientSurveyHistoryItem>({
    columns,
    data: testsQuery.data ?? [],
    totalCount,
    pagination,
    onPaginationChange: setPagination,
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
  });

  return {
    table,
    tests: testsQuery.data ?? [],
    totalCount,
    isLoading: testsQuery.isFetching,
  };
};
